/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-lines */
// deps
import { useCallback, useContext, useMemo, useState } from "react";

// internal-deps
import { AnalyticsContext, DELIVERY_SECTION_ELEMENT } from "@swiggy-private/checkout-core";
import {
    DELIVERY_SPEED,
    DELIVERY_SPEED_STRATEGY,
    DeliverySpeedDetail,
    MROChildCart,
    MROChildLocalCart,
    MRONativeLocalCart,
    Option,
    ReviewedCart,
    UserAddress,
} from "@swiggy-private/cart-types";
import { CART_STATUS_CODES } from "@swiggy-private/cart-ui";
import { captureError, useEffectOnce } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

// helpers
import {
    DeliverySpeedAppStorage,
    canShowDeliverySpeedCrouton as canShowDeliverySpeedCroutonHelper,
    checkServiceability,
    getDeliverySpeedStorageValues,
    getDeliverySpeedWidgetInfoV2,
    getSelectedSlaFromSlaOptions,
    resetCroutonCountStorageValue,
    setDeliverySpeedStorageValues,
} from "@cart-client/helpers/CartDeliverySpeed";
import { isUserLoggedIn } from "@cart-client/helpers/User";

// analytics
import { clickCartNoRushNudge, clickDeliverySpeedItem, impressionCartNoRushNudge } from "@cart-client/analytics/events";

// constants
import { APP_STORAGE_KEYS_FOR_DELIVERY_SPEED } from "@cart-client/constants/CartDeliverySpeed";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { DeliverySectionContext } from "@cart-client/contexts/DeliverySectionContext";
import {
    effectiveLocalCartComputer,
    getEffectiveCartData,
    getLocalCartId,
    getMROChildCartRestaurantId,
    isMROFlow,
    normaliseId,
} from "@cart-client/helpers/MROFoodCart";
export interface UseCartDeliverySpeedReturnType {
    hideDeliverySpeedCrouton: VoidFunction;
    onDeliverySpeedItemClick: (item: DeliverySpeedDetail, fromHalfCard?: boolean, text?: string) => void;
    deliverySpeedCroutonText?: string;
    deliverySpeedCroutonSubText?: string;
    deliverySpeedCroutonIcon?: string;
    selectedSlaOption: DeliverySpeedDetail | undefined;
    onCroutonClick: VoidFunction;
    onCroutonLoad: VoidFunction;
    onOptInWidgetClick: (optInValue: boolean) => void;
    deliverySpeedWidgetInfoV2: {
        options: Option[];
        optInText?: string;
        slaComms?: { slaText?: string; slaLogo?: string };
    } | null;
    onOfferTimerEnds?: VoidFunction;
    selectedAddress?: UserAddress;
    isRestaurantUnservicable: boolean;
}

export interface UseCartDeliverySpeedProps {
    cartResponse: ReviewedCart | MROChildCart | null;
    canShowDeliverySpeedCrouton?: boolean;
    setDeliverySpeedCroutonActive?: (isDeliverySpeedCroutonActive: boolean) => void;
}

export function useCartDeliverySpeed({
    cartResponse,
    canShowDeliverySpeedCrouton,
    setDeliverySpeedCroutonActive,
}: UseCartDeliverySpeedProps): UseCartDeliverySpeedReturnType {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { cartState } = useContext(CartContext);
    const { localCart: parentLocalCart } = cartState;
    const { updateCartInProgress, doUpdateCart } = useContext(UpdateCartContext);
    const { setDeliveryTab } = useContext(DeliverySectionContext);

    const localCart = useMemo(
        () =>
            effectiveLocalCartComputer(
                parentLocalCart as MRONativeLocalCart,
                isMROFlow(),
                getMROChildCartRestaurantId(cartResponse as MROChildCart),
            ),
        [parentLocalCart, cartResponse],
    );

    const { selectedAddress, restaurantId } = localCart || {};
    const cartId = getLocalCartId(localCart);
    const { sla_options: slaOptions, selected_sla_option: selectedSlaValue, id: addressId } = selectedAddress || {};
    const [deliverySpeedStorageData, setDeliverySpeedStorageData] = useState<DeliverySpeedAppStorage>({
        [APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.SID]: "",
        [APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT]: 0,
    });
    const userLoggedIn = isUserLoggedIn();

    const triggerUpdateCart = useCallback(
        async (slaOption: DELIVERY_SPEED) => {
            try {
                if (updateCartInProgress) return;

                if (isMROFlow() && !isEmpty(restaurantId)) {
                    const mroParentLocalCart = parentLocalCart as MRONativeLocalCart;
                    const updatedChildCarts = (mroParentLocalCart?.child_carts ?? []).map((child: MROChildLocalCart) =>
                        getMROChildCartRestaurantId(child) === normaliseId(restaurantId)
                            ? {
                                  ...child,
                                  selected_sla_option: slaOption,
                                  customer_selected_sla_option: slaOption,
                              }
                            : child,
                    );
                    await doUpdateCart({ ...mroParentLocalCart, child_carts: updatedChildCarts });
                } else {
                    await doUpdateCart({
                        ...parentLocalCart,
                        selected_sla_option: slaOption,
                        customer_selected_sla_option: slaOption,
                    });
                }
            } catch (err) {
                captureError(err);
            }
        },
        [updateCartInProgress, doUpdateCart, parentLocalCart, restaurantId],
    );

    const onDeliverySpeedItemClick = useCallback(
        (item: DeliverySpeedDetail) => {
            clickDeliverySpeedItem({
                type: item?.type,
                clickEvent,
                cartId: cartId,
                addressId,
                restaurantId: restaurantId,
            });
            if (item?.strategy?.toUpperCase() === DELIVERY_SPEED_STRATEGY.VISIBLE) {
                triggerUpdateCart(item.type);
            }
        },
        [clickEvent, cartId, addressId, restaurantId, triggerUpdateCart],
    );

    const onOptInWidgetClick = useCallback(
        async (optInValue: boolean) => {
            try {
                if (!updateCartInProgress) {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    await doUpdateCart({ ...parentLocalCart, delivery_type_opt_in: optInValue });

                    if (!optInValue) {
                        resetCroutonCountStorageValue();
                    }
                }
            } catch (err) {
                captureError(err);
            }
        },
        [doUpdateCart, parentLocalCart, updateCartInProgress],
    );

    // to check whether rx is unservicable
    const isRxUnservicable = useMemo(
        () =>
            checkServiceability(
                cartResponse?.statusCode,
                { userLoggedIn, selectedAddress },
                CART_STATUS_CODES.RESTAURANT_UNSERVICEABLE,
                0,
            ),
        [cartResponse?.statusCode, selectedAddress, userLoggedIn],
    );
    // to check whether location is unservicable
    const isLocationServicable = useMemo(
        () =>
            checkServiceability(
                cartResponse?.statusCode,
                { userLoggedIn, selectedAddress },
                CART_STATUS_CODES.SUCCESS,
                1,
            ),
        [cartResponse?.statusCode, selectedAddress, userLoggedIn],
    );

    const isAddressServiceable = !!isLocationServicable;
    const isRestaurantUnservicable = !!isRxUnservicable;

    const cartData = useMemo(() => getEffectiveCartData(cartResponse, isMROFlow()), [cartResponse]);

    const deliverySpeedWidgetInfoV2 = useMemo(
        () =>
            getDeliverySpeedWidgetInfoV2(
                cartData,
                parentLocalCart,
                isAddressServiceable,
                isRestaurantUnservicable,
                updateCartInProgress,
            ),
        [cartData, parentLocalCart, isAddressServiceable, isRestaurantUnservicable, updateCartInProgress],
    );

    const canShowCrouton = useCallback(
        (storageData: DeliverySpeedAppStorage) =>
            canShowDeliverySpeedCroutonHelper({
                storageData,
                showCrouton: canShowDeliverySpeedCrouton,
                slaOptions,
            }),
        [canShowDeliverySpeedCrouton, slaOptions],
    );

    const checkCroutonVisibility = useCallback(
        (storageData: DeliverySpeedAppStorage) => {
            if (!canShowCrouton(storageData)) {
                return;
            }
            setDeliverySpeedCroutonActive?.(true);
        },
        [canShowCrouton, setDeliverySpeedCroutonActive],
    );

    const getDeliverySpeedDataFromAppStorage = useCallback(async () => {
        const deliverySpeedAppStorageData = await getDeliverySpeedStorageValues();

        if (deliverySpeedAppStorageData != null) {
            setDeliverySpeedStorageData(deliverySpeedAppStorageData);
            checkCroutonVisibility(deliverySpeedAppStorageData);
        }
    }, [checkCroutonVisibility]);

    const selectedSlaOption = useMemo(() => {
        return getSelectedSlaFromSlaOptions(slaOptions, selectedSlaValue);
    }, [selectedSlaValue, slaOptions]);

    const hideDeliverySpeedCrouton = useCallback(() => {
        setDeliverySpeedCroutonActive?.(false);
    }, [setDeliverySpeedCroutonActive]);

    const updateDeliverySpeedStorageValue = useCallback(
        (key: APP_STORAGE_KEYS_FOR_DELIVERY_SPEED) => {
            setDeliverySpeedStorageValues(key, deliverySpeedStorageData);
        },
        [deliverySpeedStorageData],
    );

    const onCroutonLoad = useCallback(() => {
        impressionCartNoRushNudge(impressionEvent, cartId, addressId);
        updateDeliverySpeedStorageValue(APP_STORAGE_KEYS_FOR_DELIVERY_SPEED.CROUTON_COUNT);
    }, [addressId, cartId, impressionEvent, updateDeliverySpeedStorageValue]);

    const deliverySpeedCroutonText = useMemo(() => {
        return cartData?.delivery_speed_crouton?.message;
    }, [cartData?.delivery_speed_crouton?.message]);

    const deliverySpeedCroutonSubText = cartData?.delivery_speed_crouton?.subtext;

    const deliverySpeedCroutonIcon = cartData?.delivery_speed_crouton?.icon;

    const onCroutonClick = useCallback(() => {
        setDeliveryTab(DELIVERY_SECTION_ELEMENT.DELIVERY_TYPE);

        clickCartNoRushNudge(clickEvent, cartId, addressId);

        hideDeliverySpeedCrouton();
    }, [addressId, cartId, clickEvent, hideDeliverySpeedCrouton, setDeliveryTab]);

    const onOfferTimerEnds = useCallback(async () => {
        try {
            await doUpdateCart({
                ...parentLocalCart,
            });
        } catch (err) {
            captureError(err);
        }
    }, [doUpdateCart, parentLocalCart]);

    useEffectOnce(() => {
        getDeliverySpeedDataFromAppStorage();
    });

    return {
        hideDeliverySpeedCrouton,
        onDeliverySpeedItemClick,
        deliverySpeedWidgetInfoV2,
        selectedSlaOption,
        deliverySpeedCroutonText,
        deliverySpeedCroutonSubText,
        deliverySpeedCroutonIcon,
        onCroutonClick,
        onCroutonLoad,
        onOptInWidgetClick,
        onOfferTimerEnds,
        selectedAddress,
        isRestaurantUnservicable,
    };
}
